import { money } from "../lib/format";
import { toneFor } from "../lib/format";
import { Loader2, Inbox, AlertTriangle, X } from "lucide-react";

export function Money({ cents, exact, className = "" }) {
  return <span className={`money ${className}`}>{money(cents, { cents: exact })}</span>;
}

export function StatusBadge({ status, className = "" }) {
  if (!status) return null;
  return (
    <span data-testid={`status-${String(status).toLowerCase().replace(/[_ ]/g, "-")}`}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${toneFor(status)} ${className}`}>
      {String(status).replace(/_/g, " ")}
    </span>
  );
}

export function Stat({ label, cents, value, sub, tone = "navy", icon: Icon, testid, action }) {
  const tones = {
    navy: "border-l-navy", green: "border-l-emerald-500", yellow: "border-l-amber-500",
    red: "border-l-rose-500", blue: "border-l-sky-500", gold: "border-l-gold",
  };
  return (
    <div data-testid={testid} className={`card-gov border-l-4 ${tones[tone] || tones.navy} p-4 sm:p-5`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
      </div>
      <div className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900 money">{cents != null ? money(cents) : value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
      {action}
    </div>
  );
}

export function PageHeader({ title, sub, right }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-navy" data-testid="page-title">{title}</h1>
        {sub && <p className="mt-1 text-sm text-slate-500">{sub}</p>}
      </div>
      {right && <div className="flex flex-wrap items-center gap-2">{right}</div>}
    </div>
  );
}

export function Loading({ label = "Loading" }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-slate-500" data-testid="loading">
      <Loader2 className="h-5 w-5 animate-spin" /> {label}…
    </div>
  );
}

export function Empty({ title = "Nothing here yet", msg, action }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-slate-300 bg-slate-50 py-12 text-center" data-testid="empty-state">
      <Inbox className="h-8 w-8 text-slate-300" />
      <div className="font-semibold text-slate-600">{title}</div>
      {msg && <div className="max-w-sm text-sm text-slate-500">{msg}</div>}
      {action}
    </div>
  );
}

export function ErrBox({ msg }) {
  if (!msg) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" data-testid="error-box">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{msg}</span>
    </div>
  );
}

export function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-dark/60 p-4" onClick={onClose} data-testid="modal-overlay">
      <div className={`max-h-[90vh] w-full ${wide ? "max-w-3xl" : "max-w-lg"} overflow-y-auto rounded-md bg-white shadow-xl`}
        onClick={(e) => e.stopPropagation()} data-testid="modal">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="font-semibold text-navy">{title}</h3>
          <button onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100" data-testid="modal-close" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, children, hint }) {
  return (
    <div>
      <label className="label-gov">{label}</label>
      {children}
      {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
    </div>
  );
}

export function ProgressBar({ pct, tone = "green" }) {
  const colors = { green: "bg-emerald-500", yellow: "bg-amber-500", red: "bg-rose-500", blue: "bg-sky-500", navy: "bg-navy" };
  const p = Math.max(0, Math.min(100, pct || 0));
  return (
    <div className="h-3 w-full overflow-hidden rounded-full bg-slate-200" data-testid="progress-bar">
      <div className={`h-full rounded-full transition-all ${colors[tone]}`} style={{ width: `${p}%` }} />
    </div>
  );
}

export function Table({ headers, children, testid }) {
  return (
    <div className="card-gov overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-left">
            {headers.map((h, i) => (
              <th key={i} className="whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
